import { FormEvent, useEffect, useMemo, useRef, useState } from "react"
import { Head, usePage } from "@inertiajs/react"
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { CheckoutAccountStep } from "@/components/CheckoutAccountStep"
import { CheckoutLayout } from "@/components/CheckoutLayout"
import { CheckoutProgress } from "@/components/CheckoutProgress"
import { PurchaseDetailsCard, type QuoteLine } from "@/components/PurchaseDetailsCard"
import { StripeProvider } from "@/components/StripeProvider"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { fetchJson, formatCents } from "@/lib/fetch-json"
import type { PageProps } from "@/types/inertia"

interface Tier {
  up_to: number
  unit_amount: number
  label: string
}

interface Quote {
  quantity: number
  lines: QuoteLine[]
  subtotal_cents: number
  discount_cents: number
  total_cents: number
  coupon: CouponState | null
}

interface CouponState {
  code: string
  valid: boolean
  description?: string
  // Stripe coupon duration: "once" | "repeating" | "forever". Only "forever"
  // discounts recur on renewal; the others apply to the first year only.
  duration?: string
}

type BuyProps = PageProps<{
  stripe_publishable_key: string
  tiers: Tier[]
  max_seats: number
  coupon_param: string | null
  seats_param: number | null
  payment_method_types: string[]
  renews_on: string
  logged_in_email: string | null
}>

function localBreakdown(tiers: Tier[], quantity: number): QuoteLine[] {
  let remaining = quantity
  let previousCap = 0
  const lines: QuoteLine[] = []
  for (const tier of tiers) {
    if (remaining <= 0) break
    const count = Math.min(remaining, tier.up_to - previousCap)
    lines.push({
      label: tier.label,
      quantity: count,
      unit_amount: tier.unit_amount,
      amount: count * tier.unit_amount,
    })
    remaining -= count
    previousCap = tier.up_to
  }
  return lines
}

export default function Buy() {
  const { props } = usePage<BuyProps>()
  const { tiers, max_seats: maxSeats, coupon_param: couponParam } = props
  const authenticated = !!props.logged_in_email

  const [quantity, setQuantity] = useState(() =>
    Math.min(Math.max(props.seats_param ?? 1, 1), maxSeats),
  )
  const [mode, setMode] = useState<"register" | "login">("register")

  const showCoupon = couponParam !== null
  const initialCouponCode = couponParam && couponParam !== "true" ? couponParam : ""
  const [appliedCoupon, setAppliedCoupon] = useState<CouponState | null>(null)
  const [couponInput, setCouponInput] = useState(initialCouponCode)
  // Validated coupon wins; fall back to the raw input so the register/login
  // redirect round-trips what the visitor typed even mid-validation.
  const couponCode = appliedCoupon?.valid ? appliedCoupon.code : couponInput

  const [quote, setQuote] = useState<Quote | null>(null)

  const fallbackLines = useMemo(() => localBreakdown(tiers, quantity), [tiers, quantity])
  const lines = quote?.lines ?? fallbackLines
  const subtotal = quote?.subtotal_cents ?? fallbackLines.reduce((sum, l) => sum + l.amount, 0)
  const discount = quote?.discount_cents ?? 0
  const total = quote?.total_cents ?? subtotal

  // A "forever" coupon keeps the discounted price on every renewal; a "once" or
  // "repeating" coupon only discounts the first year, so renewals bill the full
  // price. When that's the case, renewals cost the undiscounted subtotal.
  const foreverDiscount = appliedCoupon?.valid && appliedCoupon.duration === "forever"
  const renewalTotal = discount > 0 && !foreverDiscount ? subtotal : total

  // Debounced authoritative quote from Stripe
  const quoteTimer = useRef<number | null>(null)
  useEffect(() => {
    if (quoteTimer.current) window.clearTimeout(quoteTimer.current)
    quoteTimer.current = window.setTimeout(async () => {
      const coupon = appliedCoupon?.valid ? appliedCoupon.code : ""
      const result = await fetchJson<Quote>("/checkout/quote", {
        body: { quantity, coupon },
      })
      if (result.ok) setQuote(result.data)
    }, 350)
    return () => {
      if (quoteTimer.current) window.clearTimeout(quoteTimer.current)
    }
  }, [quantity, appliedCoupon])

  return (
    <>
      <Head title="Buy Flywrite">
        <meta
          name="description"
          content="Get Flywrite for Mac — annual seats with volume pricing. Each seat is a license key for one person."
        />
        <meta property="og:title" content="Buy Flywrite" />
        <meta
          property="og:description"
          content="Get Flywrite for Mac — annual seats with volume pricing. Each seat is a license key for one person."
        />
      </Head>
      <CheckoutLayout
        mainHeader={
          <CheckoutProgress
            step={authenticated ? 2 : 1}
            authenticated={authenticated || mode === "login"}
          />
        }
        main={
          <>
            {!authenticated ? (
              <CheckoutAccountStep
                mode={mode}
                onModeChange={setMode}
                registerIntro="Create your Flywrite account first — it's where your license keys and billing live."
                loginIntro="Log in to your Flywrite account to continue."
                extraParams={{ seats: quantity, coupon: couponCode }}
              />
            ) : (
              <StripeProvider
                publishableKey={props.stripe_publishable_key}
                deferred={{
                  mode: "subscription",
                  amount: total,
                  currency: "usd",
                  paymentMethodTypes: props.payment_method_types,
                }}
              >
                <PaymentStep
                  email={props.logged_in_email!}
                  quantity={quantity}
                  couponCode={appliedCoupon?.valid ? appliedCoupon.code : ""}
                  totalCents={total}
                  firstYearOnly={renewalTotal !== total}
                />
              </StripeProvider>
            )}
            {showCoupon && (
              <div className="mt-6 border-t border-hairline pt-6">
                <CouponField
                  initialValue={initialCouponCode}
                  onApply={setAppliedCoupon}
                  onInputChange={setCouponInput}
                />
              </div>
            )}
          </>
        }
        sidebarHeader={
          <span className="text-sm font-medium text-ink-muted">Purchase Details</span>
        }
        sidebar={
          <PurchaseDetailsCard
            seats={quantity}
            maxSeats={maxSeats}
            onSeatsChange={setQuantity}
            lines={lines}
            discount={discount}
            discountLabel={appliedCoupon?.valid ? (appliedCoupon.description ?? null) : null}
            total={total}
            renewalTotal={renewalTotal}
            renewsOn={props.renews_on}
          />
        }
      />
    </>
  )
}

function PaymentStep({
  email,
  quantity,
  couponCode,
  totalCents,
  firstYearOnly,
}: {
  email: string
  quantity: number
  couponCode: string
  totalCents: number
  firstYearOnly: boolean
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // One incomplete subscription per (quantity, coupon) combination — a failed
  // payment retries against the same intent instead of minting a new one.
  const secretCache = useRef(new Map<string, string>())

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setProcessing(true)
    setError(null)

    // 1. Validate the Payment Element before touching the server.
    const { error: submitError } = await elements.submit()
    if (submitError) {
      setError(submitError.message ?? "Please complete your payment details.")
      setProcessing(false)
      return
    }

    // 2. Create the incomplete subscription and get its confirmation secret.
    const cacheKey = `${quantity}:${couponCode}`
    let clientSecret = secretCache.current.get(cacheKey)
    if (!clientSecret) {
      const result = await fetchJson<{ client_secret?: string; errors?: Record<string, string> }>(
        "/checkout/subscription",
        { body: { quantity, coupon: couponCode } },
      )
      if (!result.ok || !result.data.client_secret) {
        const errors = result.data.errors
        setError(
          errors?.base ?? errors?.quantity ?? "Something went wrong. Please try again.",
        )
        setProcessing(false)
        return
      }
      clientSecret = result.data.client_secret
      secretCache.current.set(cacheKey, clientSecret)
    }

    // 3. Confirm. Success redirects to /checkout/complete; failure falls through.
    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: { return_url: `${window.location.origin}/checkout/complete` },
    })
    setError(confirmError?.message ?? "Payment failed. Please try again.")
    setProcessing(false)
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <p className="text-sm text-ink-muted">
        Purchasing as <strong className="text-ink-body">{email}</strong>
      </p>
      <PaymentElement
        options={{
          layout: "tabs",
          wallets: { applePay: "never", googlePay: "never", link: "never" },
        }}
      />
      {error && <p className="text-sm text-danger-display">{error}</p>}
      <Button type="submit" disabled={!stripe || processing} className="w-full sm:w-auto">
        {processing
          ? "Processing…"
          : `Subscribe · ${formatCents(totalCents)}${firstYearOnly ? "" : "/year"}`}
      </Button>
      <p className="text-xs text-ink-muted">
        Secured by Stripe — email support@flywrite.app.
      </p>
    </form>
  )
}

// Inline-validating coupon field, shown only when a `coupon` query param is
// present. No Apply button — validation fires debounced on change and
// immediately on mount when the URL carried a code.
function CouponField({
  initialValue,
  onApply,
  onInputChange,
}: {
  initialValue: string
  onApply: (coupon: CouponState | null) => void
  onInputChange: (raw: string) => void
}) {
  const [value, setValue] = useState(initialValue)
  const [status, setStatus] = useState<"idle" | "checking" | "valid" | "invalid">("idle")
  const [description, setDescription] = useState<string | null>(null)
  const debounceRef = useRef<number | null>(null)
  const seqRef = useRef(0)

  async function validate(raw: string) {
    const trimmed = raw.trim()
    const seq = ++seqRef.current

    if (!trimmed) {
      setStatus("idle")
      setDescription(null)
      onApply(null)
      return
    }

    setStatus("checking")
    const result = await fetchJson<{ coupon?: CouponState }>("/checkout/validate_coupon", {
      body: { coupon: trimmed },
    })
    if (seq !== seqRef.current) return // a newer request superseded this one

    const coupon = result.data.coupon
    if (result.ok && coupon?.valid) {
      setStatus("valid")
      setDescription(coupon.description ?? "Discount applied")
      onApply(coupon)
    } else {
      setStatus("invalid")
      setDescription(null)
      onApply(coupon ?? { code: trimmed, valid: false })
    }
  }

  function handleChange(next: string) {
    setValue(next)
    onInputChange(next)
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => void validate(next), 400)
  }

  useEffect(() => {
    if (initialValue.trim()) void validate(initialValue)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-2">
      <label htmlFor="coupon">Coupon code</label>
      <Input
        id="coupon"
        className="max-w-xs"
        value={value}
        aria-invalid={status === "invalid"}
        onChange={(e) => handleChange(e.target.value)}
      />
      {status === "checking" && <p className="text-xs text-ink-muted">Checking…</p>}
      {status === "valid" && description && (
        <p className="text-xs text-accent">{description} applied.</p>
      )}
      {status === "invalid" && (
        <p className="text-xs text-danger-display">
          That coupon isn't valid. You can still check out without it.
        </p>
      )}
    </div>
  )
}
